import React, { useRef } from "react";

import { ensureThemeJsonArray, getDvSourceImageUrl } from "../../../functions/themeAssets";
import { MATCH_PAIR_IMAGE_DIMENSIONS, validateImageDimensions } from "./imageDimensions";

export const MATCH_PAIR_MIN = 3;
export const MATCH_PAIR_MAX = 6;
export const MATCH_PAIR_ANSWER_MAX = 30;
const MATCH_PAIR_IMAGE_EXT = ["jpg", "jpeg", "png", "gif"];
const MATCH_PAIR_IMAGE_MAX_MB = 5;

let matchPairIdCounter = 0;
export function emptyMatchPair() {
  matchPairIdCounter += 1;
  return { id: `mp-${matchPairIdCounter}`, image: "", answer: "", file: null, preview: "" };
}

export function normalizeMatchPairsFromTheme(raw) {
  const list = ensureThemeJsonArray(raw);
  if (!list.length) {
    return Array.from({ length: MATCH_PAIR_MIN }, emptyMatchPair);
  }
  return list.map((item) => ({
    ...emptyMatchPair(),
    image: String(item?.image ?? "").trim(),
    answer: String(item?.answer ?? "").slice(0, MATCH_PAIR_ANSWER_MAX),
  }));
}

export function validateMatchPairImage(file) {
  if (!file) return { ok: false, message: "Please choose an image." };
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!MATCH_PAIR_IMAGE_EXT.includes(ext)) {
    return {
      ok: false,
      message: `Unsupported format for ${file.name}. Use ${MATCH_PAIR_IMAGE_EXT.join(", ")}.`,
    };
  }
  if (file.size > MATCH_PAIR_IMAGE_MAX_MB * 1024 * 1024) {
    return { ok: false, message: `${file.name} exceeds ${MATCH_PAIR_IMAGE_MAX_MB}MB.` };
  }
  return { ok: true };
}

/** First problem with the pair list, or null when it can be saved. */
export function validateMatchPairs(pairs) {
  if (pairs.length < MATCH_PAIR_MIN) {
    return `Please add at least ${MATCH_PAIR_MIN} pairs.`;
  }
  if (pairs.length > MATCH_PAIR_MAX) {
    return `You can add up to ${MATCH_PAIR_MAX} pairs.`;
  }
  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i];
    if (!p.file && !p.image) return `Pair ${i + 1} — please upload an image.`;
    const answer = p.answer.trim();
    if (!answer) return `Pair ${i + 1} — please enter the answer text.`;
    if (answer.length > MATCH_PAIR_ANSWER_MAX) {
      return `Pair ${i + 1} — answer must be ${MATCH_PAIR_ANSWER_MAX} characters or fewer.`;
    }
  }
  const names = pairs.map((p) => p.answer.trim().toLowerCase());
  if (new Set(names).size !== names.length) {
    return "Each answer must be different so players can tell the pairs apart.";
  }
  return null;
}

export default function MatchPairTab({
  pairs,
  setPairs,
  isSaving,
  onSave,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const fileInputs = useRef({});

  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const addPair = () => {
    if (pairs.length >= MATCH_PAIR_MAX) {
      setErrorMessage(`You can add up to ${MATCH_PAIR_MAX} pairs.`);
      return;
    }
    clearMessages();
    setPairs((prev) => [...prev, emptyMatchPair()]);
  };

  const removePair = (id) => {
    if (pairs.length <= MATCH_PAIR_MIN) {
      setErrorMessage(`You need at least ${MATCH_PAIR_MIN} pairs.`);
      return;
    }
    clearMessages();
    setPairs((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const updateAnswer = (id, value) => {
    clearMessages();
    setPairs((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, answer: value.slice(0, MATCH_PAIR_ANSWER_MAX) } : p
      )
    );
  };

  const pickImage = async (id, file) => {
    if (!file) return;
    const check = validateMatchPairImage(file);
    if (!check.ok) {
      setErrorMessage(check.message);
      return;
    }
    const sizeCheck = await validateImageDimensions(file, MATCH_PAIR_IMAGE_DIMENSIONS);
    if (!sizeCheck.ok) {
      setErrorMessage(sizeCheck.message);
      return;
    }
    clearMessages();
    setPairs((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        if (p.preview) URL.revokeObjectURL(p.preview);
        return { ...p, file, preview: URL.createObjectURL(file) };
      })
    );
  };

  const clearAll = () => {
    pairs.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    setPairs(Array.from({ length: MATCH_PAIR_MIN }, emptyMatchPair));
    clearMessages();
  };

  return (
    <div className="themeupdate-panel-wide match-pair-panel" data-tour="tour-gc-match-pair">
      <p className="qa-char-limit-hint">
        * Add {MATCH_PAIR_MIN}–{MATCH_PAIR_MAX} pairs. Each pair needs an image (JPG, PNG or GIF,
        exactly {MATCH_PAIR_IMAGE_DIMENSIONS[0]}×{MATCH_PAIR_IMAGE_DIMENSIONS[1]} px, max{" "}
        {MATCH_PAIR_IMAGE_MAX_MB} MB) and its answer text (max {MATCH_PAIR_ANSWER_MAX}{" "}
        characters). Players match each image to its answer.
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="match-pair-list">
        {pairs.map((pair, index) => {
          const imageUrl = pair.preview || (pair.image ? getDvSourceImageUrl(pair.image) : "");
          return (
            <div key={pair.id} className="match-pair-row">
              <span className="match-pair-index">{index + 1}</span>

              <button
                type="button"
                className="match-pair-image-btn"
                onClick={() => fileInputs.current[pair.id]?.click()}
                disabled={isSaving}
                aria-label={`Upload image for pair ${index + 1}`}
                title="Upload image"
              >
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="match-pair-image" />
                ) : (
                  <span className="match-pair-image-empty">
                    <i className="fa-solid fa-image" aria-hidden />
                    Upload image
                  </span>
                )}
              </button>
              <input
                ref={(el) => {
                  fileInputs.current[pair.id] = el;
                }}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                className="match-pair-file-input"
                onChange={(e) => {
                  pickImage(pair.id, e.target.files?.[0]);
                  e.target.value = "";
                }}
                disabled={isSaving}
              />

              <i className="fa-solid fa-arrows-left-right match-pair-link" aria-hidden />

              <div className="match-pair-answer">
                <label htmlFor={`mp-answer-${pair.id}`}>Answer</label>
                <input
                  id={`mp-answer-${pair.id}`}
                  type="text"
                  value={pair.answer}
                  maxLength={MATCH_PAIR_ANSWER_MAX}
                  onChange={(e) => updateAnswer(pair.id, e.target.value)}
                  placeholder="e.g. Peony"
                  disabled={isSaving}
                />
                <span className="match-pair-count">
                  {pair.answer.length}/{MATCH_PAIR_ANSWER_MAX}
                </span>
              </div>

              <button
                type="button"
                className="match-pair-delete"
                onClick={() => removePair(pair.id)}
                disabled={isSaving || pairs.length <= MATCH_PAIR_MIN}
                aria-label={`Delete pair ${index + 1}`}
                title="Delete pair"
              >
                <i className="fa-regular fa-trash-can" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="qa-pairs-actions">
        <button
          type="button"
          className="qa-btn qa-btn-outline"
          onClick={addPair}
          disabled={isSaving || pairs.length >= MATCH_PAIR_MAX}
        >
          Add Pair
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={onSave} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save and Continue"}
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={clearAll} disabled={isSaving}>
          Clear All
        </button>
      </div>
    </div>
  );
}
